// Shared person create/edit form — PROPS-DRIVEN. No API/query imports: pages
// own the data fetching, wire `initial` + `onSubmit`, and pass `geoapifyKey`
// from the auth/config payload. Government IDs are NOT edited here (managed on
// the detail surface). Sections gate on the viewer's permissions via can():
//   Identity + Contact  — always shown
//   Physical            — person.read_pii
//   Emergency contacts  — person.read_pii
// Address rows offer Geoapify autocomplete when a key is present; manual entry
// is always available, and a missing key hides the autocomplete UI entirely.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import type {
  CreatePersonAddressInput,
  CreatePersonEmailInput,
  CreatePersonEmergencyContactInput,
  CreatePersonInput,
  CreatePersonPhoneInput,
  EmergencyRelationship,
  EyeColor,
  HairColor,
  PersonAddressLabel,
  PersonDetail,
  PersonEmailLabel,
  PersonPhoneLabel,
  PersonSuffix,
  PersonTitle,
} from '@dsr/shared-types';
import { formatPhoneDisplay } from '@dsr/shared-types';
import { can } from '../auth/store';
import { DateInput } from './DateInput';
import { PhoneInput } from './PhoneInput';
import { Badge, Button, FormField, Input, Textarea } from './ui';

// ---------- Public payload shape ----------

export interface PersonFormPayload {
  person: CreatePersonInput;
  addresses: CreatePersonAddressInput[];
  phones: CreatePersonPhoneInput[];
  emails: CreatePersonEmailInput[];
  emergency_contacts: CreatePersonEmergencyContactInput[];
}

export interface PersonFormProps {
  initial?: PersonDetail | null;
  onSubmit: (payload: PersonFormPayload) => void | Promise<void>;
  submitting?: boolean;
  geoapifyKey?: string | null;
}

// ---------- Enum option tables (mirror the shared-types CHECK enums) ----------

const TITLE_OPTIONS: { value: PersonTitle; label: string }[] = [
  { value: 'mr', label: 'Mr.' },
  { value: 'ms', label: 'Ms.' },
  { value: 'mrs', label: 'Mrs.' },
  { value: 'dr', label: 'Dr.' },
  { value: 'mx', label: 'Mx.' },
  { value: 'other', label: 'Other' },
];

const SUFFIX_OPTIONS: { value: PersonSuffix; label: string }[] = [
  { value: 'jr', label: 'Jr.' },
  { value: 'sr', label: 'Sr.' },
  { value: 'ii', label: 'II' },
  { value: 'iii', label: 'III' },
  { value: 'iv', label: 'IV' },
  { value: 'other', label: 'Other' },
];

const EYE_OPTIONS: { value: EyeColor; label: string }[] = [
  { value: 'brown', label: 'Brown' },
  { value: 'blue', label: 'Blue' },
  { value: 'green', label: 'Green' },
  { value: 'hazel', label: 'Hazel' },
  { value: 'gray', label: 'Gray' },
  { value: 'amber', label: 'Amber' },
  { value: 'other', label: 'Other' },
];

const HAIR_OPTIONS: { value: HairColor; label: string }[] = [
  { value: 'black', label: 'Black' },
  { value: 'brown', label: 'Brown' },
  { value: 'blond', label: 'Blond' },
  { value: 'red', label: 'Red' },
  { value: 'gray', label: 'Gray' },
  { value: 'white', label: 'White' },
  { value: 'bald', label: 'Bald' },
  { value: 'other', label: 'Other' },
];

const ADDRESS_LABEL_OPTIONS: { value: PersonAddressLabel; label: string }[] = [
  { value: 'home', label: 'Home' },
  { value: 'mailing', label: 'Mailing' },
  { value: 'work', label: 'Work' },
  { value: 'residence', label: 'Residence' },
  { value: 'previous', label: 'Previous' },
  { value: 'other', label: 'Other' },
];

const PHONE_LABEL_OPTIONS: { value: PersonPhoneLabel; label: string }[] = [
  { value: 'mobile', label: 'Mobile' },
  { value: 'home', label: 'Home' },
  { value: 'work', label: 'Work' },
  { value: 'other', label: 'Other' },
];

const EMAIL_LABEL_OPTIONS: { value: PersonEmailLabel; label: string }[] = [
  { value: 'personal', label: 'Personal' },
  { value: 'work', label: 'Work' },
  { value: 'company', label: 'Company' },
  { value: 'other', label: 'Other' },
];

const RELATIONSHIP_OPTIONS: { value: EmergencyRelationship; label: string }[] = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'parent', label: 'Parent' },
  { value: 'child', label: 'Child' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'partner', label: 'Partner' },
  { value: 'friend', label: 'Friend' },
  { value: 'other', label: 'Other' },
];

// ---------- Editable row shapes (local form state; _key for stable React keys) ----------

interface AddrRow {
  _key: string;
  label: PersonAddressLabel;
  street1: string;
  street2: string;
  city: string;
  state: string;
  postal_code: string;
  is_primary: boolean;
  formatted: string | null;
  lat: number | null;
  lng: number | null;
}

interface PhoneRow {
  _key: string;
  label: PersonPhoneLabel;
  number: string;
  extension: string;
  is_primary: boolean;
}

interface EmailRow {
  _key: string;
  label: PersonEmailLabel;
  email: string;
  is_primary: boolean;
}

interface ContactRow {
  _key: string;
  first_name: string;
  last_name: string;
  relationship: EmergencyRelationship;
  phone: string;
  alt_phone: string;
  email: string;
  priority: string;
}

let keySeq = 0;
function nextKey(): string {
  keySeq += 1;
  return `r${keySeq}`;
}

function emptyAddr(): AddrRow {
  return {
    _key: nextKey(),
    label: 'home',
    street1: '',
    street2: '',
    city: '',
    state: '',
    postal_code: '',
    is_primary: false,
    formatted: null,
    lat: null,
    lng: null,
  };
}

function emptyPhone(): PhoneRow {
  return { _key: nextKey(), label: 'mobile', number: '', extension: '', is_primary: false };
}

function emptyEmail(): EmailRow {
  return { _key: nextKey(), label: 'personal', email: '', is_primary: false };
}

function emptyContact(): ContactRow {
  return {
    _key: nextKey(),
    first_name: '',
    last_name: '',
    relationship: 'spouse',
    phone: '',
    alt_phone: '',
    email: '',
    priority: '1',
  };
}

// ---------- Geoapify autocomplete ----------

interface GeoSuggestion {
  formatted: string;
  street1: string;
  city: string;
  state: string;
  postal_code: string;
  lat: number | null;
  lng: number | null;
}

interface GeoFeatureProps {
  formatted?: string;
  housenumber?: string;
  street?: string;
  city?: string;
  state_code?: string;
  state?: string;
  postcode?: string;
  lat?: number;
  lon?: number;
}

function toSuggestion(p: GeoFeatureProps): GeoSuggestion {
  const street1 = [p.housenumber, p.street].filter(Boolean).join(' ').trim();
  return {
    formatted: p.formatted ?? '',
    street1,
    city: p.city ?? '',
    state: p.state_code ?? p.state ?? '',
    postal_code: p.postcode ?? '',
    lat: typeof p.lat === 'number' ? p.lat : null,
    lng: typeof p.lon === 'number' ? p.lon : null,
  };
}

function AddressAutocomplete({
  apiKey,
  onPick,
}: {
  apiKey: string;
  onPick: (s: GeoSuggestion) => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<GeoSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
      abortRef.current?.abort();
    };
  }, []);

  const run = (q: string): void => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(() => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setLoading(true);
      const url =
        `https://api.geoapify.com/v1/geocode/autocomplete` +
        `?text=${encodeURIComponent(q)}&filter=countrycode:us&apiKey=${encodeURIComponent(apiKey)}`;
      fetch(url, { signal: ac.signal })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((data: { features?: { properties?: GeoFeatureProps }[] }) => {
          const next = (data.features ?? [])
            .map((f) => f.properties)
            .filter((p): p is GeoFeatureProps => p != null)
            .map(toSuggestion);
          setSuggestions(next);
          setOpen(next.length > 0);
        })
        .catch(() => {
          /* network/verify failure is non-blocking — manual entry stays available */
        })
        .finally(() => setLoading(false));
    }, 300);
  };

  return (
    <div className="form-field form-field-full">
      <span>Address lookup</span>
      <input
        className="form-input"
        value={text}
        placeholder="Start typing a US address…"
        autoComplete="off"
        onChange={(e) => {
          setText(e.target.value);
          run(e.target.value);
        }}
        onFocus={() => {
          if (suggestions.length > 0) setOpen(true);
        }}
      />
      {loading && <p className="small muted">Searching…</p>}
      {open && suggestions.length > 0 && (
        <ul className="tag-list" role="listbox">
          {suggestions.map((s, i) => (
            <li key={`${s.formatted}-${i}`}>
              <Button
                variant="ghost"
                className="btn-sm"
                onClick={() => {
                  onPick(s);
                  setText(s.formatted);
                  setOpen(false);
                }}
              >
                {s.formatted}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Main form ----------

export function PersonForm(props: PersonFormProps): JSX.Element {
  const { initial, onSubmit, submitting = false, geoapifyKey } = props;

  const canPii = can('person.read_pii');

  // Identity
  const [title, setTitle] = useState<PersonTitle | ''>(initial?.title ?? '');
  const [firstName, setFirstName] = useState(initial?.first_name ?? '');
  const [middleName, setMiddleName] = useState(initial?.middle_name ?? '');
  const [lastName, setLastName] = useState(initial?.last_name ?? '');
  const [suffix, setSuffix] = useState<PersonSuffix | ''>(initial?.suffix ?? '');
  const [preferredName, setPreferredName] = useState(initial?.preferred_name ?? '');
  const [dob, setDob] = useState<number | null>(initial?.dob ?? null);

  // Physical (PII)
  const [heightIn, setHeightIn] = useState(
    initial?.height_in != null ? String(initial.height_in) : '',
  );
  const [weightLb, setWeightLb] = useState(
    initial?.weight_lb != null ? String(initial.weight_lb) : '',
  );
  const [eyeColor, setEyeColor] = useState<EyeColor | ''>(initial?.eye_color ?? '');
  const [hairColor, setHairColor] = useState<HairColor | ''>(initial?.hair_color ?? '');
  const [marks, setMarks] = useState(initial?.distinguishing_marks ?? '');

  // Contact lists
  const [addresses, setAddresses] = useState<AddrRow[]>(
    (initial?.addresses ?? []).map((a) => ({
      _key: nextKey(),
      label: a.label,
      street1: a.street1,
      street2: a.street2 ?? '',
      city: a.city,
      state: a.state ?? '',
      postal_code: a.postal_code ?? '',
      is_primary: a.is_primary,
      formatted: a.formatted,
      lat: a.lat != null ? Number(a.lat) : null,
      lng: a.lng != null ? Number(a.lng) : null,
    })),
  );
  const [phones, setPhones] = useState<PhoneRow[]>(
    (initial?.phones ?? []).map((p) => ({
      _key: nextKey(),
      label: p.label,
      number: formatPhoneDisplay(p.number),
      extension: p.extension ?? '',
      is_primary: p.is_primary,
    })),
  );
  const [emails, setEmails] = useState<EmailRow[]>(
    (initial?.emails ?? []).map((e) => ({
      _key: nextKey(),
      label: e.label,
      email: e.email,
      is_primary: e.is_primary,
    })),
  );
  const [contacts, setContacts] = useState<ContactRow[]>(
    (initial?.emergency_contacts ?? []).map((c) => ({
      _key: nextKey(),
      first_name: c.first_name,
      last_name: c.last_name,
      relationship: c.relationship,
      phone: formatPhoneDisplay(c.phone),
      alt_phone: c.alt_phone ? formatPhoneDisplay(c.alt_phone) : '',
      email: c.email ?? '',
      priority: String(c.priority),
    })),
  );

  const [formError, setFormError] = useState<string | null>(null);

  // --- per-category single-primary helpers ---
  const setAddrPrimary = (key: string): void =>
    setAddresses((rows) => rows.map((r) => ({ ...r, is_primary: r._key === key })));
  const setPhonePrimary = (key: string): void =>
    setPhones((rows) => rows.map((r) => ({ ...r, is_primary: r._key === key })));
  const setEmailPrimary = (key: string): void =>
    setEmails((rows) => rows.map((r) => ({ ...r, is_primary: r._key === key })));

  const patchAddr = (key: string, patch: Partial<AddrRow>): void =>
    setAddresses((rows) => rows.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  const patchPhone = (key: string, patch: Partial<PhoneRow>): void =>
    setPhones((rows) => rows.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  const patchEmail = (key: string, patch: Partial<EmailRow>): void =>
    setEmails((rows) => rows.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  const patchContact = (key: string, patch: Partial<ContactRow>): void =>
    setContacts((rows) => rows.map((r) => (r._key === key ? { ...r, ...patch } : r)));

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setFormError(null);
    if (!firstName.trim() || !lastName.trim()) {
      setFormError('First and last name are required.');
      return;
    }

    const person: CreatePersonInput = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      ...(title ? { title } : {}),
      ...(middleName.trim() ? { middle_name: middleName.trim() } : {}),
      ...(suffix ? { suffix } : {}),
      ...(preferredName.trim() ? { preferred_name: preferredName.trim() } : {}),
      ...(dob != null ? { dob } : {}),
    };
    if (canPii) {
      const h = Number(heightIn);
      const w = Number(weightLb);
      if (heightIn.trim() && Number.isInteger(h) && h > 0) person.height_in = h;
      if (weightLb.trim() && Number.isInteger(w) && w > 0) person.weight_lb = w;
      if (eyeColor) person.eye_color = eyeColor;
      if (hairColor) person.hair_color = hairColor;
      if (marks.trim()) person.distinguishing_marks = marks.trim();
    }

    const addressPayload: CreatePersonAddressInput[] = addresses
      .filter((a) => a.street1.trim() || a.city.trim())
      .map((a) => ({
        label: a.label,
        street1: a.street1.trim(),
        city: a.city.trim(),
        ...(a.street2.trim() ? { street2: a.street2.trim() } : {}),
        ...(a.state.trim() ? { state: a.state.trim() } : {}),
        ...(a.postal_code.trim() ? { postal_code: a.postal_code.trim() } : {}),
        is_primary: a.is_primary,
        ...(a.formatted ? { formatted: a.formatted } : {}),
        ...(a.lat != null ? { lat: a.lat } : {}),
        ...(a.lng != null ? { lng: a.lng } : {}),
        ...(a.formatted && a.lat != null && a.lng != null
          ? { verified_at: Date.now() }
          : {}),
      }));

    const phonePayload: CreatePersonPhoneInput[] = phones
      .filter((p) => p.number.trim())
      .map((p) => ({
        label: p.label,
        number: p.number.trim(),
        ...(p.extension.trim() ? { extension: p.extension.trim() } : {}),
        is_primary: p.is_primary,
      }));

    const emailPayload: CreatePersonEmailInput[] = emails
      .filter((e2) => e2.email.trim())
      .map((e2) => ({
        label: e2.label,
        email: e2.email.trim(),
        is_primary: e2.is_primary,
      }));

    const contactPayload: CreatePersonEmergencyContactInput[] = canPii
      ? contacts
          .filter((c) => c.first_name.trim() && c.phone.trim())
          .map((c) => {
            const pr = Number(c.priority);
            return {
              first_name: c.first_name.trim(),
              last_name: c.last_name.trim(),
              relationship: c.relationship,
              phone: c.phone.trim(),
              ...(c.alt_phone.trim() ? { alt_phone: c.alt_phone.trim() } : {}),
              ...(c.email.trim() ? { email: c.email.trim() } : {}),
              ...(Number.isInteger(pr) && pr > 0 ? { priority: pr } : {}),
            };
          })
      : [];

    void onSubmit({
      person,
      addresses: addressPayload,
      phones: phonePayload,
      emails: emailPayload,
      emergency_contacts: contactPayload,
    });
  };

  return (
    <form className="person-form" onSubmit={handleSubmit}>
      {/* ---------- Identity ---------- */}
      <section className="detail-card">
        <div className="section-header">
          <h2>Identity</h2>
        </div>
        <div className="identity-grid">
          <FormField label="Title">
            <select
              className="form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value as PersonTitle | '')}
            >
              <option value="">—</option>
              {TITLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="First name" required>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </FormField>
          <FormField label="Middle name">
            <Input
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value)}
            />
          </FormField>
          <FormField label="Last name" required>
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </FormField>
          <FormField label="Suffix">
            <select
              className="form-input"
              value={suffix}
              onChange={(e) => setSuffix(e.target.value as PersonSuffix | '')}
            >
              <option value="">—</option>
              {SUFFIX_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Preferred name" className="identity-span-3">
            <Input
              value={preferredName}
              onChange={(e) => setPreferredName(e.target.value)}
            />
          </FormField>
          <FormField label="Date of birth" className="identity-span-2">
            <DateInput value={dob} onChange={setDob} />
          </FormField>
        </div>
      </section>

      {/* ---------- Contact: addresses ---------- */}
      <section className="detail-card">
        <div className="section-header">
          <h2>Addresses</h2>
          <Button
            variant="primary"
            className="btn-sm"
            onClick={() => setAddresses((rows) => [...rows, emptyAddr()])}
          >
            Add address
          </Button>
        </div>
        {addresses.length === 0 && <p className="muted">No addresses added.</p>}
        {addresses.map((a) => (
          <div key={a._key} className="contact-row">
            <div className="form-grid">
              <FormField label="Label">
                <select
                  className="form-input"
                  value={a.label}
                  onChange={(e) =>
                    patchAddr(a._key, { label: e.target.value as PersonAddressLabel })
                  }
                >
                  {ADDRESS_LABEL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Primary">
                <span className="checkbox-field">
                  <input
                    type="radio"
                    name="addr-primary"
                    checked={a.is_primary}
                    onChange={() => setAddrPrimary(a._key)}
                  />
                  <span>Primary address</span>
                </span>
              </FormField>
              {geoapifyKey && (
                <AddressAutocomplete
                  apiKey={geoapifyKey}
                  onPick={(s) =>
                    patchAddr(a._key, {
                      street1: s.street1 || a.street1,
                      city: s.city || a.city,
                      state: s.state || a.state,
                      postal_code: s.postal_code || a.postal_code,
                      formatted: s.formatted || null,
                      lat: s.lat,
                      lng: s.lng,
                    })
                  }
                />
              )}
              <FormField label="Street" full>
                <Input
                  value={a.street1}
                  onChange={(e) => patchAddr(a._key, { street1: e.target.value })}
                />
              </FormField>
              <FormField label="Street 2" full>
                <Input
                  value={a.street2}
                  onChange={(e) => patchAddr(a._key, { street2: e.target.value })}
                />
              </FormField>
              <FormField label="City">
                <Input
                  value={a.city}
                  onChange={(e) => patchAddr(a._key, { city: e.target.value })}
                />
              </FormField>
              <FormField label="State">
                <Input
                  value={a.state}
                  maxLength={2}
                  onChange={(e) =>
                    patchAddr(a._key, { state: e.target.value.toUpperCase() })
                  }
                />
              </FormField>
              <FormField label="ZIP">
                <Input
                  value={a.postal_code}
                  onChange={(e) => patchAddr(a._key, { postal_code: e.target.value })}
                />
              </FormField>
            </div>
            <div className="contact-row-actions">
              {a.formatted && <Badge tone="success">Verified</Badge>}
              <Button
                variant="ghost"
                className="btn-sm"
                onClick={() =>
                  setAddresses((rows) => rows.filter((r) => r._key !== a._key))
                }
              >
                Remove
              </Button>
            </div>
          </div>
        ))}
      </section>

      {/* ---------- Contact: phones ---------- */}
      <section className="detail-card">
        <div className="section-header">
          <h2>Phones</h2>
          <Button
            variant="primary"
            className="btn-sm"
            onClick={() => setPhones((rows) => [...rows, emptyPhone()])}
          >
            Add phone
          </Button>
        </div>
        {phones.length === 0 && <p className="muted">No phones added.</p>}
        {phones.map((p) => (
          <div key={p._key} className="contact-row">
            <div className="form-grid">
              <FormField label="Label">
                <select
                  className="form-input"
                  value={p.label}
                  onChange={(e) =>
                    patchPhone(p._key, { label: e.target.value as PersonPhoneLabel })
                  }
                >
                  {PHONE_LABEL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Number">
                <PhoneInput
                  value={p.number}
                  onChange={(next) => patchPhone(p._key, { number: next })}
                />
              </FormField>
              <FormField label="Extension">
                <Input
                  value={p.extension}
                  onChange={(e) => patchPhone(p._key, { extension: e.target.value })}
                />
              </FormField>
              <FormField label="Primary">
                <span className="checkbox-field">
                  <input
                    type="radio"
                    name="phone-primary"
                    checked={p.is_primary}
                    onChange={() => setPhonePrimary(p._key)}
                  />
                  <span>Primary phone</span>
                </span>
              </FormField>
            </div>
            <div className="contact-row-actions">
              <Button
                variant="ghost"
                className="btn-sm"
                onClick={() => setPhones((rows) => rows.filter((r) => r._key !== p._key))}
              >
                Remove
              </Button>
            </div>
          </div>
        ))}
      </section>

      {/* ---------- Contact: emails ---------- */}
      <section className="detail-card">
        <div className="section-header">
          <h2>Emails</h2>
          <Button
            variant="primary"
            className="btn-sm"
            onClick={() => setEmails((rows) => [...rows, emptyEmail()])}
          >
            Add email
          </Button>
        </div>
        {emails.length === 0 && <p className="muted">No emails added.</p>}
        {emails.map((em) => (
          <div key={em._key} className="contact-row">
            <div className="form-grid">
              <FormField label="Label">
                <select
                  className="form-input"
                  value={em.label}
                  onChange={(e) =>
                    patchEmail(em._key, { label: e.target.value as PersonEmailLabel })
                  }
                >
                  {EMAIL_LABEL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Email">
                <Input
                  type="email"
                  value={em.email}
                  onChange={(e) => patchEmail(em._key, { email: e.target.value })}
                />
              </FormField>
              <FormField label="Primary">
                <span className="checkbox-field">
                  <input
                    type="radio"
                    name="email-primary"
                    checked={em.is_primary}
                    onChange={() => setEmailPrimary(em._key)}
                  />
                  <span>Primary email</span>
                </span>
              </FormField>
            </div>
            <div className="contact-row-actions">
              <Button
                variant="ghost"
                className="btn-sm"
                onClick={() => setEmails((rows) => rows.filter((r) => r._key !== em._key))}
              >
                Remove
              </Button>
            </div>
          </div>
        ))}
      </section>

      {/* ---------- Physical (person.read_pii) ---------- */}
      {canPii && (
        <section className="detail-card">
          <div className="section-header">
            <h2>Physical descriptors</h2>
          </div>
          <div className="physical-grid">
            <FormField label="Height (in)">
              <Input
                type="number"
                min={1}
                value={heightIn}
                onChange={(e) => setHeightIn(e.target.value)}
              />
            </FormField>
            <FormField label="Weight (lb)">
              <Input
                type="number"
                min={1}
                value={weightLb}
                onChange={(e) => setWeightLb(e.target.value)}
              />
            </FormField>
            <FormField label="Eye color">
              <select
                className="form-input"
                value={eyeColor}
                onChange={(e) => setEyeColor(e.target.value as EyeColor | '')}
              >
                <option value="">—</option>
                {EYE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Hair color">
              <select
                className="form-input"
                value={hairColor}
                onChange={(e) => setHairColor(e.target.value as HairColor | '')}
              >
                <option value="">—</option>
                {HAIR_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Distinguishing marks" full>
              <Textarea
                rows={2}
                value={marks}
                onChange={(e) => setMarks(e.target.value)}
              />
            </FormField>
          </div>
        </section>
      )}

      {/* ---------- Emergency contacts (person.read_pii) ---------- */}
      {canPii && (
        <section className="detail-card">
          <div className="section-header">
            <h2>Emergency contacts</h2>
            <Button
              variant="primary"
              className="btn-sm"
              onClick={() => setContacts((rows) => [...rows, emptyContact()])}
            >
              Add contact
            </Button>
          </div>
          {contacts.length === 0 && <p className="muted">No emergency contacts added.</p>}
          {contacts.map((c) => (
            <div key={c._key} className="contact-row">
              <div className="form-grid">
                <FormField label="First name">
                  <Input
                    value={c.first_name}
                    onChange={(e) => patchContact(c._key, { first_name: e.target.value })}
                  />
                </FormField>
                <FormField label="Last name">
                  <Input
                    value={c.last_name}
                    onChange={(e) => patchContact(c._key, { last_name: e.target.value })}
                  />
                </FormField>
                <FormField label="Relationship">
                  <select
                    className="form-input"
                    value={c.relationship}
                    onChange={(e) =>
                      patchContact(c._key, {
                        relationship: e.target.value as EmergencyRelationship,
                      })
                    }
                  >
                    {RELATIONSHIP_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Priority">
                  <Input
                    type="number"
                    min={1}
                    value={c.priority}
                    onChange={(e) => patchContact(c._key, { priority: e.target.value })}
                  />
                </FormField>
                <FormField label="Phone">
                  <PhoneInput
                    value={c.phone}
                    onChange={(next) => patchContact(c._key, { phone: next })}
                  />
                </FormField>
                <FormField label="Alt phone">
                  <PhoneInput
                    value={c.alt_phone}
                    onChange={(next) => patchContact(c._key, { alt_phone: next })}
                  />
                </FormField>
                <FormField label="Email" full>
                  <Input
                    type="email"
                    value={c.email}
                    onChange={(e) => patchContact(c._key, { email: e.target.value })}
                  />
                </FormField>
              </div>
              <div className="contact-row-actions">
                <Button
                  variant="ghost"
                  className="btn-sm"
                  onClick={() =>
                    setContacts((rows) => rows.filter((r) => r._key !== c._key))
                  }
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </section>
      )}

      {formError && (
        <p className="form-error error-text" role="alert">
          {formError}
        </p>
      )}

      <div className="header-actions">
        <Button type="submit" variant="primary" disabled={submitting}>
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
